/**
 * Contribution lifecycle status is mutable after Stripe settles a payment:
 * refunds, disputes, and later accounting stages must not turn the original
 * payment back into an unsettled attempt.
 */
export const SETTLED_STRIPE_CONTRIBUTION_STATUSES = [
  "completed",
  "synced",
  "error",
  "pending_sync",
  "needs_allocation",
  "reconciled",
  "posted",
  "partially_refunded",
  "refunded",
  "disputed",
] as const;

const SETTLED_STRIPE_STATUSES = new Set<string>(
  SETTLED_STRIPE_CONTRIBUTION_STATUSES,
);

// These sources represent payment attempts rather than staff-confirmed gifts.
// Their pending/processing rows remain visible activity but cannot become
// giving or accounting input until the processor reports a settled state.
export type PaymentProcessorContributionSource = "stripe" | "recurring";

const PAYMENT_PROCESSOR_BATCH_SOURCES = new Set<PaymentProcessorContributionSource>([
  "stripe",
  "recurring",
]);

export const NON_COUNTABLE_GIVING_CONTRIBUTION_STATUSES = [
  "void",
  "voided",
  "refunded",
  "reversed",
  "failed",
  "canceled",
  "cancelled",
] as const;

const NON_COUNTABLE_CONTRIBUTION_STATUSES = new Set<string>(
  NON_COUNTABLE_GIVING_CONTRIBUTION_STATUSES,
);

const normalized = (value: string | null | undefined): string =>
  (value ?? "").trim().toLowerCase();

export const canonicalPaymentProcessorContributionSource = (
  batchSource: string | null | undefined,
): PaymentProcessorContributionSource | null => {
  const source = normalized(batchSource);
  return PAYMENT_PROCESSOR_BATCH_SOURCES.has(source as PaymentProcessorContributionSource)
    ? source as PaymentProcessorContributionSource
    : null;
};

export type PaymentProcessorSettlementStatus = "completed" | "failed";

export const planPaymentProcessorSettlement = (
  batchSource: string | null | undefined,
  currentStatus: string | null | undefined,
  targetStatus: PaymentProcessorSettlementStatus,
  authoritativeAmount?: number | null,
): {
  batchSource: PaymentProcessorContributionSource;
  status: PaymentProcessorSettlementStatus;
  amount?: number;
  grossAmount?: number;
} | null => {
  const canonicalBatchSource = canonicalPaymentProcessorContributionSource(batchSource);
  const normalizedCurrentStatus = normalized(currentStatus);
  const transitionAllowed = targetStatus === "failed"
    ? normalizedCurrentStatus === "pending"
    : normalizedCurrentStatus === "pending" || normalizedCurrentStatus === "failed";
  const hasAuthoritativeAmount = authoritativeAmount !== null && authoritativeAmount !== undefined;
  const amountAllowed = !hasAuthoritativeAmount || (
    targetStatus === "completed" &&
    Number.isFinite(authoritativeAmount) &&
    authoritativeAmount > 0 &&
    authoritativeAmount <= 999999.99 &&
    Math.round(authoritativeAmount * 100) / 100 === authoritativeAmount
  );

  return canonicalBatchSource && transitionAllowed && amountAllowed
    ? {
      batchSource: canonicalBatchSource,
      status: targetStatus,
      ...(hasAuthoritativeAmount
        ? { amount: authoritativeAmount, grossAmount: authoritativeAmount }
        : {}),
    }
    : null;
};

export const isPaymentProcessorContributionSource = (
  batchSource: string | null | undefined,
): boolean => canonicalPaymentProcessorContributionSource(batchSource) !== null;

export const isSettledStripeContribution = (
  status: string | null | undefined,
  batchSource: string | null | undefined,
): boolean =>
  !isPaymentProcessorContributionSource(batchSource) ||
  SETTLED_STRIPE_STATUSES.has(normalized(status));

export const isUnsettledStripeContribution = (
  status: string | null | undefined,
  batchSource: string | null | undefined,
): boolean =>
  isPaymentProcessorContributionSource(batchSource) &&
  !isSettledStripeContribution(status, batchSource);

export const shouldOverwriteStripeContributionAmount = (
  status: string | null | undefined,
  batchSource: string | null | undefined,
): boolean =>
  isPaymentProcessorContributionSource(batchSource) &&
  ["pending", "failed"].includes(normalized(status));

/**
 * Staff-entered cash and checks stay outside giving totals until their review
 * is completed. Processor attempts enter totals only after settlement. Once
 * a payment settles, accounting workflow states such as pending_sync and
 * needs_allocation remain countable. A full refund remains settled history but
 * is excluded from giving; a partial refund remains a countable contribution
 * with its refund lifecycle status preserved.
 */
export const isCountableGivingContribution = (
  status: string | null | undefined,
  batchSource: string | null | undefined,
): boolean =>
  !NON_COUNTABLE_CONTRIBUTION_STATUSES.has(normalized(status))
  && normalized(status) !== "pending"
  &&
  isSettledStripeContribution(status, batchSource);

export interface CountableGivingContributionRecord {
  status?: string | null;
  batch_source?: string | null;
  deleted_at?: string | null;
  void_reason?: string | null;
}

/**
 * Apply row lifecycle guards that sit outside the status predicate. Service
 * role readers can see soft-deleted rows, while legacy voids may retain a
 * countable-looking status, so callers that admit whole contribution rows must
 * use this record-level guard.
 */
export const isCountableGivingContributionRecord = (
  contribution: CountableGivingContributionRecord | null | undefined,
): boolean =>
  Boolean(contribution) &&
  !contribution?.deleted_at &&
  !normalized(contribution?.void_reason) &&
  isCountableGivingContribution(
    contribution?.status,
    contribution?.batch_source,
  );
